import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { IDKitWidget, VerificationLevel, ISuccessResult } from '@worldcoin/idkit';
import { MiniKit } from '@worldcoin/minikit-js';
import gun from '../../config/gun.config';
import { WORLDCOIN_APP_ID, WORLDCOIN_ACTION } from '../../config/app.config';
import { StabeonUser, DEFAULT_AVATAR } from '../../types/user';
import { DEV_MODE } from '../../config/development';
import { restoreKeypairFromGUN } from '../../hooks/useSEA';
import { identifyUser, track } from '../../analytics';

export interface UserSession {
  user: StabeonUser | null;
  isLoading: boolean;
  previewMode: boolean;
  updateUser: (partial: Partial<StabeonUser>) => void;
  logout: () => void;
  exitPreview: () => void;
  loginWithCode: (code: string) => void;
}

function generateRecoveryCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No 0/O/1/I to avoid confusion
  let code = '';
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

const UserContext = createContext<UserSession>({
  user: null, isLoading: true, previewMode: false,
  updateUser: () => {}, logout: () => {}, exitPreview: () => {}, loginWithCode: () => {},
});

export const useUser = () => useContext(UserContext);

function makeDevUser(): StabeonUser {
  const devId = localStorage.getItem('stabeon_dev_id') || `dev_${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem('stabeon_dev_id', devId);
  return {
    nullifierHash: devId,
    nickname: `Traveller_${devId.slice(-6)}`,
    avatar: { ...DEFAULT_AVATAR, bgColor: `hsl(${devId.charCodeAt(4) * 37 % 360}, 60%, 50%)`, emoji: '👤' },
    lat: null, lng: null, locationPrecision: 'exact',
    activity: 'Just arrived', activityCategory: 'other',
    isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(),
    visibilityRadius: null, badges: [], streak: 0,
    lastActivityDate: null, totalActivityDays: 0, level: 1, experience: 0,
  };
}

function safeParseBadges(raw: any): string[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') { try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

function safeParseAvatar(data: any, fallback: any): any {
  if (!data) return fallback;
  if (typeof data === 'object' && data.emoji) return data;
  try { return JSON.parse(data); } catch { return fallback; }
}

/** Returns profilePhoto only when it is a valid data URL string — guards against GUN soul-link objects. */
function safePhoto(raw: any): string | undefined {
  return typeof raw === 'string' && raw.startsWith('data:') ? raw : undefined;
}

const GUN_TIMEOUT_MS = 3000;

const TAGLINE_COMPLETIONS = [
  'your running buddy',
  'tonight\'s plans',
  'your study group',
  'a new best friend',
  'your next adventure',
  'someone who gets it',
  'the concert crew',
  'your coworking spot',
];

function RotatingTagline() {
  const [index, setIndex] = React.useState(0);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % TAGLINE_COMPLETIONS.length);
        setVisible(true);
      }, 400);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="mt-4 text-center h-12 flex flex-col items-center justify-center">
      <p className="text-stabeon-accent text-sm font-medium italic">
        You are only one event away from finding...
      </p>
      <p
        className={`text-stabeon-text text-base font-bold mt-1 transition-all duration-300 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        {TAGLINE_COMPLETIONS[index]}
      </p>
    </div>
  );
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<StabeonUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [showCodeLogin, setShowCodeLogin] = useState(false);
  const [codeInput, setCodeInput] = useState('');

  const exitPreview = useCallback(() => {
    setPreviewMode(false);
    setAuthError(null);
    setVerifying(false);
    setShowCodeLogin(false);
    setCodeInput('');
  }, []);

  // Session restore
  useEffect(() => {
    const saved = localStorage.getItem('stabeon_session');
    if (!saved) { setIsLoading(false); return; }
    let parsed: StabeonUser;
    try { parsed = JSON.parse(saved); }
    catch { localStorage.removeItem('stabeon_session'); setIsLoading(false); return; }

    let resolved = false;
    gun.get('stabeon_users').get(parsed.nullifierHash).once((data: any) => {
      if (resolved) return;
      resolved = true;
      const baseAvatar = safeParseAvatar(data?.avatar, parsed.avatar);
      // Merge the separately-stored profilePhoto back into the avatar object.
      // safePhoto() guards against GUN soul-link objects being returned instead of the string.
      const photo = safePhoto(data?.profilePhoto) || safePhoto(parsed.avatar?.profilePhoto);
      const avatar = photo ? { ...baseAvatar, profilePhoto: photo } : baseAvatar;
      // localStorage (parsed) wins over GUN on restore — prevents stale GUN data
      // from overwriting a nickname the user just changed on this device.
      setUser({ ...(data || {}), ...parsed, nullifierHash: parsed.nullifierHash, avatar, badges: safeParseBadges(data?.badges ?? parsed.badges) });
      setIsLoading(false);
    });
    const t = setTimeout(() => { if (!resolved) { resolved = true; setUser(parsed); setIsLoading(false); } }, GUN_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  // Track last-written profile snapshot to avoid flooding GUN with identical data.
  // Only location-volatile fields (lat/lng/heading/isMoving/activity/lastSeen) are
  // written on every call. Heavy profile fields (nickname, avatar, bio, badges,
  // level, interests) are written only when they actually change.
  const lastProfileSig = React.useRef('');

  const persistUser = useCallback((u: StabeonUser, locationOnly = false) => {
    // ── 1. localStorage (always) ─────────────────────────────────────────────
    try {
      localStorage.setItem('stabeon_session', JSON.stringify(u));
    } catch {
      try {
        const slim = { ...u, avatar: { ...(u.avatar || {}), profilePhoto: undefined } };
        localStorage.setItem('stabeon_session', JSON.stringify(slim));
      } catch (e) {
        console.warn('[persist] localStorage full', e);
      }
    }

    if (!u.avatar || !u.nullifierHash) return;

    const userNode = gun.get('stabeon_users').get(u.nullifierHash);

    // ── 2. Location-volatile fields (written every call) ─────────────────────
    // These are the only fields that change frequently (every 2-5s).
    // Writing them field-by-field avoids overwriting other fields and keeps
    // the write surface minimal to reduce relay load and peer flooding.
    try {
      userNode.get('lat').put(u.lat as any);
      userNode.get('lng').put(u.lng as any);
      userNode.get('heading').put(u.heading as any);
      userNode.get('isMoving').put(u.isMoving as any);
      userNode.get('activity').put(u.activity as any);
      userNode.get('activityCategory').put(u.activityCategory as any);
      userNode.get('isOnline').put(true as any);
      userNode.get('lastSeen').put(Date.now() as any);
    } catch (e) {
      console.warn('[persist] GUN location write failed', e);
    }

    if (locationOnly) return;

    // ── 3. Profile fields — only when changed ────────────────────────────────
    // Build a signature of profile-only fields. Skip the write if nothing changed
    // to avoid flooding the relay with identical objects every 2 seconds.
    const { profilePhoto, ...avatarShape } = u.avatar;
    const profileSig = [
      u.nickname, JSON.stringify(avatarShape), u.bio || '',
      u.level, u.experience, u.streak,
      JSON.stringify(u.badges || []),
      JSON.stringify(u.interests || []),
      u.locationPrecision, u.visibilityRadius,
      u.city || '', u.country || '',
      u.lookingFor || '', u.matchOnboarded || false, u.age || '',
    ].join('|');

    if (profileSig === lastProfileSig.current) return;
    lastProfileSig.current = profileSig;

    try {
      // Write profile fields individually — more reliable CRDT propagation
      // than a bulk .put() which can silently drop fields on some relays.
      userNode.get('nickname').put(u.nickname as any);
      userNode.get('avatar').put(JSON.stringify(avatarShape) as any);
      userNode.get('badges').put(JSON.stringify(u.badges || []) as any);
      userNode.get('level').put(u.level as any);
      userNode.get('experience').put(u.experience as any);
      userNode.get('streak').put(u.streak as any);
      userNode.get('locationPrecision').put(u.locationPrecision as any);
      userNode.get('visibilityRadius').put((u.visibilityRadius ?? null) as any);
      userNode.get('nullifierHash').put(u.nullifierHash as any);
      userNode.get('recoveryCode').put((u.recoveryCode || '') as any);
      userNode.get('onboarded').put((u.onboarded ?? false) as any);
      if (u.bio !== undefined) userNode.get('bio').put((u.bio || '') as any);
      if (u.city)    userNode.get('city').put(u.city as any);
      if (u.country) userNode.get('country').put(u.country as any);
      if (u.interests && u.interests.length > 0) {
        userNode.get('interests').put(JSON.stringify(u.interests) as any);
      }
      if (u.lookingFor)      userNode.get('lookingFor').put(u.lookingFor as any);
      if (u.matchOnboarded !== undefined) userNode.get('matchOnboarded').put(u.matchOnboarded as any);
      if (u.age)             userNode.get('age').put(u.age as any);
      if (u.gender)          userNode.get('gender').put(u.gender as any);
      // profilePhoto: field-level write avoids soul-link issues with large base64
      if (profilePhoto) {
        userNode.get('profilePhoto').put(profilePhoto as any);
      }
      console.log('[persist] Profile written to GUN');
    } catch (e) {
      console.warn('[persist] GUN profile write failed', e);
    }
  }, []);

  const initUser = useCallback((nullifierHash: string) => {
    setAuthError(null);
    setVerifying(false);
    const existing = localStorage.getItem('stabeon_session');
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (parsed.nullifierHash === nullifierHash) {
          setUser(parsed); persistUser({ ...parsed, lastSeen: Date.now() }); setIsLoading(false); return;
        }
      } catch {}
    }
    // Generate recovery code ONCE before async — both paths use the same code
    const code = generateRecoveryCode();
    let resolved = false;

    gun.get('stabeon_users').get(nullifierHash).once((data: any) => {
      if (resolved) return; resolved = true;
      // Check if profile exists — nickname OR lastSeen means it was created before
      if (data && (data.nickname || data.lastSeen)) {
        // Existing user — restore with fallbacks for missing fields
        const seed = nullifierHash.slice(-6);
        const baseAvatar = safeParseAvatar(data.avatar, DEFAULT_AVATAR);
        const restored: StabeonUser = {
          nullifierHash,
          nickname: data.nickname || `Explorer_${seed}`,
          avatar: safePhoto(data.profilePhoto) ? { ...baseAvatar, profilePhoto: safePhoto(data.profilePhoto) } : baseAvatar,
          lat: data.lat || null, lng: data.lng || null,
          locationPrecision: data.locationPrecision || 'exact',
          activity: data.activity || '', activityCategory: data.activityCategory || 'other',
          isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(),
          visibilityRadius: data.visibilityRadius || null,
          badges: safeParseBadges(data.badges),
          streak: Number(data.streak) || 0,
          lastActivityDate: data.lastActivityDate || null,
          totalActivityDays: Number(data.totalActivityDays) || 0,
          level: Number(data.level) || 1,
          experience: Number(data.experience) || 0,
          recoveryCode: data.recoveryCode || code,
          onboarded: true,
        };
        console.log('[Auth] Existing user restored:', restored.nickname);
        identifyUser(nullifierHash, restored.nickname);
        track('user_logged_in', { method: 'worldcoin' });
        setUser(restored); persistUser(restored);
        // Ensure recovery mapping exists
        if (restored.recoveryCode) {
          gun.get('joystance_recovery').get(restored.recoveryCode).put({ hash: nullifierHash, ts: Date.now() } as any);
        }
      } else {
        // New user
        const seed = nullifierHash.slice(-6);
        const newUser: StabeonUser = {
          nullifierHash, nickname: `Explorer_${seed}`, avatar: { ...DEFAULT_AVATAR },
          lat: null, lng: null, locationPrecision: 'exact', activity: '', activityCategory: 'other',
          isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(), visibilityRadius: null,
          badges: [], streak: 0, lastActivityDate: null, totalActivityDays: 0, level: 1, experience: 0,
          recoveryCode: code,
        };
        identifyUser(nullifierHash, newUser.nickname);
        track('user_signed_up', { method: 'worldcoin' });
        setUser(newUser); persistUser(newUser);
        gun.get('joystance_recovery').get(code).put({ hash: nullifierHash, ts: Date.now() } as any);
        console.log('[Auth] Recovery code created:', code, '→', nullifierHash.slice(-8));
      }
      setIsLoading(false);
    });

    // Timeout — uses the SAME code generated above
    setTimeout(() => {
      if (resolved) return; resolved = true;
      const seed = nullifierHash.slice(-6);
      const newUser: StabeonUser = {
        nullifierHash, nickname: `Explorer_${seed}`, avatar: { ...DEFAULT_AVATAR },
        lat: null, lng: null, locationPrecision: 'exact', activity: '', activityCategory: 'other',
        isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(), visibilityRadius: null,
        badges: [], streak: 0, lastActivityDate: null, totalActivityDays: 0, level: 1, experience: 0,
        recoveryCode: code,
      };
      setUser(newUser); persistUser(newUser); setIsLoading(false);
      gun.get('joystance_recovery').get(code).put({ hash: nullifierHash, ts: Date.now() } as any);
      console.log('[Auth] Recovery code created (timeout):', code, '→', nullifierHash.slice(-8));
    }, GUN_TIMEOUT_MS);
  }, [persistUser]);

  // Login with recovery code — cross-device
  const loginInProgress = React.useRef(false);
  const loginWithCode = useCallback((code: string) => {
    const cleanCode = code.trim().toUpperCase();
    if (cleanCode.length !== 8) { setAuthError('Code must be 8 characters'); return; }
    if (loginInProgress.current) return; // Prevent re-entry
    loginInProgress.current = true;
    setVerifying(true); setAuthError(null);

    let resolved = false;

    console.log('[Auth] Looking up recovery code:', cleanCode);

    gun.get('joystance_recovery').get(cleanCode).once((raw: any) => {
      if (resolved) return;
      console.log('[Auth] Recovery lookup raw:', JSON.stringify(raw).slice(0, 200));

      let hashStr: string | null = null;

      if (!raw) {
        // Nothing found
      } else if (typeof raw === 'string') {
        hashStr = raw;
      } else if (typeof raw === 'object') {
        // Object format: { hash: "xxx", ts: 123, _: {...} }
        hashStr = raw.hash || null;
        // Fallback: scan all values for a string that looks like a hash
        if (!hashStr) {
          for (const [k, v] of Object.entries(raw)) {
            if (k === '_' || k === '#') continue;
            if (typeof v === 'string' && v.length > 5) { hashStr = v; break; }
          }
        }
      }

      if (!hashStr) {
        console.log('[Auth] No hash found for code:', cleanCode);
        setAuthError('Invalid recovery code. Check and try again.');
        setVerifying(false);
        loginInProgress.current = false;
        resolved = true;
        return;
      }

      resolved = true;
      console.log('[Auth] Found hash for code:', hashStr.slice(-8));

      // ── Restore encryption keypair FIRST ──────────────────────────────────
      // The SEA keypair is encrypted in GUN using the nullifierHash as the key.
      // Restoring it into localStorage before setUser() runs means useSEA() will
      // find the original keypair instead of generating a new one — so all DMs
      // that were encrypted on other devices will still decrypt correctly.
      restoreKeypairFromGUN(hashStr as string).then(kp => {
        if (kp) {
          localStorage.setItem('stabeon_sea_pair', JSON.stringify(kp));
          console.log('[Auth] Encryption keypair restored from GUN — DMs will decrypt correctly');
        } else {
          console.warn('[Auth] No keypair backup found — DMs from other devices may not decrypt');
        }
      });
      // ─────────────────────────────────────────────────────────────────────

      // Load the user profile
      console.log('[Auth] Loading profile for hash:', hashStr);
      gun.get('stabeon_users').get(hashStr as string).once((data: any) => {
        console.log('[Auth] Profile data:', data ? Object.keys(data).join(',') : 'NULL');
        loginInProgress.current = false;
        setVerifying(false);
        if (data && (data.nickname || data.nullifierHash)) {
          console.log('[Auth] Profile found:', data.nickname || 'no nickname');
          const baseAvatar = safeParseAvatar(data.avatar, DEFAULT_AVATAR);
          const restored: StabeonUser = {
            ...data, nullifierHash: hashStr as string,
            avatar: safePhoto(data.profilePhoto) ? { ...baseAvatar, profilePhoto: safePhoto(data.profilePhoto) } : baseAvatar,
            isOnline: true, lastSeen: Date.now(),
            recoveryCode: cleanCode, onboarded: true,
          };
          identifyUser(hashStr as string, restored.nickname);
          track('user_logged_in', { method: 'recovery_code' });
          setUser(restored); persistUser(restored);
          setShowCodeLogin(false); setCodeInput('');
        } else {
          console.log('[Auth] Profile has no nickname, creating minimal profile');
          const seed = (hashStr as string).slice(-6);
          const baseAvatar = safeParseAvatar(data?.avatar, DEFAULT_AVATAR);
          const restored: StabeonUser = {
            nullifierHash: hashStr as string,
            nickname: data?.nickname || `Explorer_${seed}`,
            avatar: safePhoto(data?.profilePhoto) ? { ...baseAvatar, profilePhoto: safePhoto(data?.profilePhoto) } : baseAvatar,
            lat: null, lng: null, locationPrecision: 'exact',
            activity: '', activityCategory: 'other',
            isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(),
            visibilityRadius: null, badges: [], streak: 0,
            lastActivityDate: null, totalActivityDays: 0, level: 1, experience: 0,
            recoveryCode: cleanCode, onboarded: true,
          };
          setUser(restored); persistUser(restored);
          setShowCodeLogin(false); setCodeInput('');
        }
      });

      // Profile lookup timeout
      setTimeout(() => {
        if (!loginInProgress.current) return;
        console.log('[Auth] Profile lookup timeout, creating new profile');
        loginInProgress.current = false;
        setVerifying(false);
        const seed = (hashStr as string).slice(-6);
        const restored: StabeonUser = {
          nullifierHash: hashStr as string,
          nickname: `Explorer_${seed}`,
          avatar: DEFAULT_AVATAR,
          lat: null, lng: null, locationPrecision: 'exact',
          activity: '', activityCategory: 'other',
          isMoving: false, heading: 0, isOnline: true, lastSeen: Date.now(),
          visibilityRadius: null, badges: [], streak: 0,
          lastActivityDate: null, totalActivityDays: 0, level: 1, experience: 0,
          recoveryCode: cleanCode,
        };
        setUser(restored); persistUser(restored);
        setShowCodeLogin(false); setCodeInput('');
      }, 4000);
    });

    // Overall timeout
    setTimeout(() => {
      if (resolved) return;
      loginInProgress.current = false;
      resolved = true;
      setVerifying(false);
      setAuthError('Could not connect to server. Check your internet and try again.');
    }, 6000);
  }, [persistUser]);

  const updateUser = useCallback((partial: Partial<StabeonUser>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...partial, lastSeen: Date.now() };
      // Location-only writes: don't re-write profile data on every GPS tick
      const isLocationOnly = Object.keys(partial).every(k =>
        ['lat','lng','heading','isMoving','activity','activityCategory','lastSeen','isOnline'].includes(k)
      );
      persistUser(next, isLocationOnly);
      // Propagate nickname change to all owned events and pins in GUN
      if (partial.nickname && partial.nickname !== prev.nickname) {
        const myId = prev.nullifierHash;
        gun.get('stabeon_events').map().once((data: any, id: string) => {
          if (data && data.creatorId === myId) {
            gun.get('stabeon_events').get(id).get('creatorNick').put(partial.nickname as string);
          }
        });
        gun.get('stabeon_pins').map().once((data: any, id: string) => {
          if (data && data.creatorId === myId) {
            gun.get('stabeon_pins').get(id).get('creatorNick').put(partial.nickname as string);
          }
        });
      }
      return next;
    });
  }, [persistUser]);

  const logout = useCallback(() => {
    if (user) {
      gun.get('stabeon_users').get(user.nullifierHash).get('isOnline').put(false);
      gun.get('stabeon_users').get(user.nullifierHash).get('lastSeen').put(Date.now());
    }
    localStorage.removeItem('stabeon_session');
    setUser(null); setPreviewMode(false);
  }, [user]);

  // ── Online heartbeat ──────────────────────────────────────────────────────
  // Publishes isOnline=true + lastSeen every 60s while the tab is visible.
  // Sets isOnline=false immediately on tab hide / page unload so the 10-min
  // "stale online" window shrinks to seconds for normal closes.
  useEffect(() => {
    if (!user) return;

    const uid = user.nullifierHash;
    const userNode = gun.get('stabeon_users').get(uid);

    const pulse = () => {
      userNode.get('isOnline').put(true);
      userNode.get('lastSeen').put(Date.now());
    };

    const goOffline = () => {
      userNode.get('isOnline').put(false);
      userNode.get('lastSeen').put(Date.now());
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') goOffline();
      else pulse();
    };

    pulse(); // Immediate pulse on login / mount
    const heartbeat = setInterval(pulse, 60_000);

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', goOffline);

    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', goOffline);
      goOffline();
    };
  }, [user?.nullifierHash]); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────

  // Refresh wallet address in GUN on every app load (World App context)
  // This ensures push notifications work even without re-verification
  useEffect(() => {
    if (!MiniKit.isInstalled() || !user?.nullifierHash) return;
    const walletAddr = (MiniKit as any).user?.walletAddress;
    if (walletAddr) {
      gun.get('stabeon_users').get(user.nullifierHash).get('walletAddress').put(walletAddr);
    }
  }, [user?.nullifierHash]);

  // MiniKit handler
  const handleMiniKit = useCallback(async () => {
    if (!MiniKit.isInstalled()) return;
    setVerifying(true); setAuthError(null);
    try {
      const resp = await MiniKit.commandsAsync.verify({ action: WORLDCOIN_ACTION, verification_level: VerificationLevel.Device as any });
      const payload = resp?.finalPayload as any;
      if (payload?.status === 'success' && payload?.nullifier_hash) {
        initUser(payload.nullifier_hash);
        // Collect wallet address for push notifications — best-effort, non-blocking
        const walletAddr = (MiniKit as any).user?.walletAddress;
        if (walletAddr) {
          gun.get('stabeon_users').get(payload.nullifier_hash).get('walletAddress').put(walletAddr);
        }
      } else {
        setAuthError('Verification was not completed.'); setVerifying(false);
      }
    } catch (e) {
      console.error('MiniKit failed', e);
      setAuthError('Could not connect to World App.'); setVerifying(false);
    }
  }, [initUser]);

  // IDKit handleVerify — called when proof is received (before onSuccess)
  const handleVerify = useCallback(async (result: ISuccessResult) => {
    // In a production app you'd send this proof to your backend for verification
    // For now we accept it client-side — the nullifier_hash is the user identity
    console.log('[Auth] Proof received, nullifier:', result.nullifier_hash.slice(-8));
    // We don't throw — if this callback doesn't throw, IDKit calls onSuccess
  }, []);

  // IDKit onSuccess — called after handleVerify succeeds
  const handleIDKitSuccess = useCallback((result: ISuccessResult) => {
    console.log('[Auth] IDKit success, logging in');
    initUser(result.nullifier_hash);
  }, [initUser]);

  // Auto-login in World App
  const autoLoginAttempted = React.useRef(false);
  useEffect(() => {
    if (autoLoginAttempted.current || user || isLoading) return;
    if (MiniKit.isInstalled()) { autoLoginAttempted.current = true; setTimeout(() => handleMiniKit(), 500); }
  }, [user, isLoading, handleMiniKit]);

  const isMiniApp = MiniKit.isInstalled();

  // Loading
  if (isLoading) {
    return (
      <div className="w-screen flex items-center justify-center bg-stabeon-bg" style={{ height: '100dvh' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-stabeon-accent border-t-transparent animate-spin" />
          <p className="text-stabeon-muted text-xs">{isMiniApp ? 'Connecting to World App...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!user && !previewMode) {
    return (
      <div className="w-screen relative overflow-hidden" style={{ height: '100dvh' }}>
        {/* Map background */}
        <div className="absolute inset-0 z-0">
          <svg className="w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
            <defs><style>{`
              .road{fill:none;stroke:rgba(124,58,237,0.12);stroke-width:2;stroke-linecap:round}
              .road-main{fill:none;stroke:rgba(124,58,237,0.18);stroke-width:3;stroke-linecap:round;stroke-dasharray:8 12;animation:df 3s linear infinite}
              .block{fill:rgba(124,58,237,0.04);stroke:rgba(124,58,237,0.08);stroke-width:1}
              .pd{fill:rgba(124,58,237,0.3)}.pp{fill:rgba(124,58,237,0.15)}
              @keyframes df{to{stroke-dashoffset:-20}}
              @keyframes fl{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
              @keyframes ps{0%,100%{r:6;opacity:.15}50%{r:14;opacity:0}}
              .a1{animation:fl 4s ease-in-out infinite}.a2{animation:fl 5s ease-in-out infinite .5s}
              .pr{animation:ps 2.5s ease-out infinite}
            `}</style></defs>
            <path className="road" d="M0 120Q200 115 400 120Q600 125 800 118"/><path className="road" d="M0 300Q200 290 400 300Q600 310 800 295"/>
            <path className="road" d="M0 500Q180 495 420 502Q620 508 800 498"/><path className="road" d="M0 700Q200 690 400 700Q600 710 800 695"/>
            <path className="road" d="M150 0Q145 200 152 400Q158 600 148 800"/><path className="road" d="M400 0Q395 200 402 400Q408 600 398 800"/>
            <path className="road" d="M650 0Q645 200 652 400Q658 600 648 800"/>
            <path className="road-main" d="M0 400Q200 390 400 400Q600 410 800 395"/>
            <path className="road-main" d="M300 0Q295 200 302 400Q308 600 298 800"/>
            <rect className="block" x="170" y="130" width="200" height="140" rx="6"/><rect className="block" x="420" y="320" width="200" height="150" rx="6"/>
            <rect className="block" x="100" y="520" width="180" height="140" rx="6"/><rect className="block" x="500" y="550" width="160" height="120" rx="6"/>
            <g className="a1"><circle className="pr pp" cx="250" cy="250" r="8"/><circle className="pd" cx="250" cy="250" r="5"/></g>
            <g className="a2"><circle className="pr pp" cx="550" cy="450" r="8"/><circle className="pd" cx="550" cy="450" r="5"/></g>
            <g className="a1"><circle className="pr pp" cx="400" cy="650" r="8"/><circle className="pd" cx="400" cy="650" r="5"/></g>
          </svg>
          <div className="absolute inset-0 bg-gradient-to-b from-stabeon-bg/60 via-stabeon-bg/40 to-stabeon-bg/80" />
        </div>

        {/* Content — single centered column */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6">
          <img src="/logo.svg" alt="Joystance" className="w-20 h-20 sm:w-24 sm:h-24 mb-4 drop-shadow-[0_0_24px_rgba(124,58,237,0.5)]" />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-stabeon-text tracking-tight">
            Joy<span className="text-stabeon-accent">stance</span>
          </h1>
          <p className="text-stabeon-sub text-lg mt-2 font-light">Go out, find joy.</p>

          {/* Animated rotating tagline */}
          <RotatingTagline />

          {/* Trust badges */}
          <div className="flex items-center gap-4 mt-5">
            <div className="flex items-center gap-1 text-stabeon-muted">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              <span className="text-[9px]">Encrypted</span>
            </div>
            <div className="flex items-center gap-1 text-stabeon-muted">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span className="text-[9px]">No data stored</span>
            </div>
            <div className="flex items-center gap-1 text-stabeon-muted">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span className="text-[9px]">Verified humans</span>
            </div>
          </div>

          {/* Auth */}
          <div className="w-full max-w-[280px] flex flex-col gap-3 mt-8 mx-auto">
            {authError && (
              <div className="text-stabeon-danger text-xs text-center bg-stabeon-danger/10 rounded-xl px-4 py-2">{authError}</div>
            )}

            {verifying && (
              <div className="flex items-center justify-center gap-2 py-4">
                <div className="w-5 h-5 rounded-full border-2 border-stabeon-accent border-t-transparent animate-spin" />
                <p className="text-stabeon-sub text-sm">Verifying...</p>
              </div>
            )}

            {!verifying && (
              <>
                {showCodeLogin ? (
                  /* Recovery code input */
                  <div className="flex flex-col gap-2">
                    <input
                      value={codeInput}
                      onChange={e => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ''))}
                      placeholder="e.g. K7XN4BPR"
                      maxLength={8}
                      className="input-field text-center text-lg font-mono tracking-[0.3em]"
                      autoFocus
                    />
                    <p className="text-stabeon-muted text-[10px] text-center">
                      Enter the 8-character code from your other device
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => { setShowCodeLogin(false); setCodeInput(''); setAuthError(null); }} className="btn-ghost flex-1 text-sm">Back</button>
                      <button onClick={() => loginWithCode(codeInput)} disabled={codeInput.length !== 8} className="btn-primary flex-1 text-sm">Login</button>
                    </div>
                  </div>
                ) : (
                  /* Main auth buttons */
                  <>
                    {isMiniApp ? (
                      <button onClick={handleMiniKit} className="btn-primary w-full py-4 text-base rounded-2xl">
                        Enter
                      </button>
                    ) : (
                      <IDKitWidget
                        app_id={WORLDCOIN_APP_ID}
                        action={WORLDCOIN_ACTION}
                        handleVerify={handleVerify}
                        onSuccess={handleIDKitSuccess}
                        verification_level={VerificationLevel.Device}
                      >
                        {({ open }) => (
                          <button onClick={open} className="btn-primary w-full py-4 text-base rounded-2xl">
                            Join now
                          </button>
                        )}
                      </IDKitWidget>
                    )}

                    {DEV_MODE && (
                      <button onClick={() => { const u = makeDevUser(); setUser(u); persistUser(u); }} className="btn-secondary w-full text-sm">
                        Dev mode
                      </button>
                    )}

                    {/* Secondary actions — compact row */}
                    <div className="flex items-center justify-center gap-4 pt-1">
                      <button onClick={() => setShowCodeLogin(true)} className="text-stabeon-muted text-[11px] hover:text-stabeon-text transition-colors">
                        Already have an account?
                      </button>
                      <span className="text-stabeon-border text-[10px]">·</span>
                      <button onClick={() => setPreviewMode(true)} className="text-stabeon-muted text-[11px] hover:text-stabeon-text transition-colors">
                        Just browse
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ user, isLoading, previewMode, updateUser, logout, exitPreview, loginWithCode }}>
      {children}
    </UserContext.Provider>
  );
}
